import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { NodeExecutionStatus, Voice } from 'types';

import { DEFAULT_VOICE, VOICE_LIST_URL } from '@/constants/defaultVoice';
import Image from 'next/image';
import VoiceSelection, {
  VOICE_PICKER_PANEL_CLASS,
} from '@/components/speech/VoicesModal';
import { TipTapPromptEditor } from '../TipTapPromptEditor';
import Button from '@/components/buttons/Button';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { getVoiceImageUrl } from '@/utils/voices';
import { NodeActionsDropdown } from './NodeActionsDropdown';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface TextToSpeechConfig {
  voice: string;
  voiceID: string;
  text: string;
}

interface TextToSpeechNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

export function TextToSpeechNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: TextToSpeechNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState<boolean>(
    !(typeof (data as { result?: unknown })?.result === 'string'),
  );
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  // Preset voices fetched from the API so the ~25k-line VOICES constant stays
  // out of the client bundle.
  const [voices, setVoices] = useState<Voice[]>([DEFAULT_VOICE]);
  const [voicesLoading, setVoicesLoading] = useState(true);

  useEffect(() => {
    fetch(VOICE_LIST_URL)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d?.voices)) setVoices(d.voices);
      })
      .catch((e) => console.error('Failed to load voices', e))
      .finally(() => setVoicesLoading(false));
  }, []);

  const executionStatus = ((s) => {
    return typeof s === 'string' &&
      ['idle', 'running', 'success', 'error'].includes(s)
      ? (s as NodeExecutionStatus)
      : undefined;
  })((data as { status?: unknown })?.status);

  const isRunning = executionStatus === 'running';

  const config: TextToSpeechConfig = (data.config as TextToSpeechConfig) || {
    voice: DEFAULT_VOICE.name,
    voiceID: DEFAULT_VOICE.voice_id,
    text: '',
  };

  // Auto-collapse settings when result is generated
  useEffect(() => {
    if (
      typeof (data as { result?: unknown })?.result === 'string' &&
      showSettings
    ) {
      setShowSettings(false);
    }
  }, [data.result]);

  // Hide result if running
  const shouldShowResult =
    typeof (data as { result?: unknown })?.result === 'string' && !isRunning;

  // The voice picker calls setVoice and setVoiceID back to back, before a
  // re-render: read the latest config from the store so the second update
  // doesn't put back the old voice name from this render's snapshot.
  const updateVoiceConfig = (updates: Partial<TextToSpeechConfig>) => {
    const currentNode = useWorkflowStore
      .getState()
      .nodes.find((n) => n.id === id);
    const currentConfig =
      (currentNode?.data?.config as Partial<TextToSpeechConfig>) || config;
    updateNodeData(id, { config: { ...currentConfig, ...updates } });
  };

  const handleSetVoice = (voiceName: string) => {
    updateVoiceConfig({ voice: voiceName });
  };

  const handleSetVoiceID = (voiceID: string) => {
    updateVoiceConfig({ voiceID });
  };

  const handleTextChange = (value: string) => {
    updateNodeData(id, {
      config: {
        ...config,
        text: value,
      },
    });
  };

  const selectedVoice =
    voices.find((v) => v.voice_id === config.voiceID) || DEFAULT_VOICE;

  const rawError = (data as any)?.error;
  const errorText =
    typeof rawError === 'string'
      ? rawError.trim()
      : rawError != null
        ? String(rawError).trim()
        : '';

  // Connected text nodes for mention autocomplete
  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) =>
        edge.target === id &&
        (edge.targetHandle === 'prompt' || edge.targetHandle === 'text'),
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        return {
          id: sourceNode?.id || '',
          label: (sourceNode?.data?.label as string) || 'Node',
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  return (
    <>
      <BaseNode
        title={
          (typeof (data as { label?: unknown })?.label === 'string' &&
            (data as { label?: string }).label) ||
          t('Text to Speech')
        }
        icon={
          <svg
            className='w-4 h-4 text-indigo-600'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' />
          </svg>
        }
        hasInput={false}
        hasOutput={false}
        status={((s) => {
          return typeof s === 'string' &&
            ['idle', 'running', 'success', 'error'].includes(s)
            ? (s as NodeExecutionStatus)
            : undefined;
        })((data as { status?: unknown })?.status)}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        customInputs={createInputHandle('text', 'text', 50)}
        customOutputs={createOutputHandle('audio', 'audio', 50)}
        hideToolbarActions
        customHeaderButtons={
          <>
            {data.result && (
              <SimpleTooltip
                title={showSettings ? t('Hide settings') : t('Show settings')}
                side='top'
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings(!showSettings);
                  }}
                  className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
                >
                  <svg
                    className='w-5 h-5 text-gray-600 dark:text-gray-300'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                </button>
              </SimpleTooltip>
            )}
            <NodeActionsDropdown
              nodeId={nodeId || id}
              nodeTitle={(data.label as string) || t('Text to Speech')}
              mediaUrl={data.result as string}
              mediaType='audio'
              filename='generated-audio.mp3'
            />
          </>
        }
      >
        <div>
          {/* Collapsible Settings Section */}
          <div
            className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
              showSettings ? '' : 'hidden'
            }`}
          >
            {/* Voice Selection */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Voice')}
              </label>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVoiceModal(true);
                }}
                className='w-full flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
              >
                <Image
                  src={getVoiceImageUrl(selectedVoice?.image)}
                  alt={selectedVoice?.name || t('Voice')}
                  width='50'
                  height='50'
                  className='rounded-lg'
                />
                <div className='flex-1 text-left'>
                  <p className='text-sm font-medium text-gray-900 dark:text-white'>
                    {selectedVoice?.name || t('Select Voice')}
                  </p>
                  {selectedVoice?.language && (
                    <p className='text-xs text-gray-500 dark:text-gray-400'>
                      {Array.isArray(selectedVoice.language)
                        ? selectedVoice.language[0]
                        : selectedVoice.language}
                      {selectedVoice.accent && ` (${selectedVoice.accent})`}
                    </p>
                  )}
                </div>
                <svg
                  className='w-5 h-5 text-gray-400 dark:text-gray-500'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M9 5l7 7-7 7' />
                </svg>
              </button>
            </div>

            {/* Text Input */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Text (optional if connected)')}
              </label>
              <TipTapPromptEditor
                value={config.text || ''}
                onChange={handleTextChange}
                connectedNodes={getConnectedTextNodes()}
                placeholder={t(
                  'Enter text... Type @ to reference connected nodes',
                )}
              />
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                {t('Or connect text from another node')}
              </p>
            </div>

            {/* Run Button (inside settings) */}
            <div className='pt-1'>
              <Button
                className='w-full justify-center'
                variant='outline'
                isLoading={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode?.(nodeId || id);
                }}
              >
                {data.result ? (
                  <>
                    <RefreshIcon className='w-4 h-4 mr-2' />
                    <span>{t('Regenerate')}</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className='w-4 h-4 mr-2' />
                    <span>{t('Run')}</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Result Preview - Audio Player */}
          {shouldShowResult && (
            <div className='p-3'>
              <div className='bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4'>
                <audio
                  src={data.result as string}
                  controls
                  className='w-full'
                />
              </div>
            </div>
          )}

          {/* Error Display */}
          {errorText && (
            <div className='mx-3 mb-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-2 rounded'>
              <p className='text-xs text-red-600 dark:text-red-400'>
                {errorText}
              </p>
            </div>
          )}
        </div>
      </BaseNode>

      {/* Voice Selection Modal - Rendered via Portal for full width */}
      {showVoiceModal &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <div
            className='fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[9999]'
            onClick={(e) => {
              e.stopPropagation();
              setShowVoiceModal(false);
            }}
          >
            <div
              className={`w-[95vw] overflow-hidden bg-white shadow-xl dark:bg-slate-900 ${VOICE_PICKER_PANEL_CLASS}`}
              onClick={(e) => e.stopPropagation()}
            >
              <VoiceSelection
                voices={voices}
                setVoice={handleSetVoice}
                setVoiceID={handleSetVoiceID}
                currentVoice={config.voice}
                currentVoiceId={config.voiceID}
                closeModal={() => setShowVoiceModal(false)}
                isLoading={voicesLoading}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
